import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Star, Phone, Mail, Facebook, MessageSquare, DollarSign, Eye, StickyNote, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { QuickEnquiryModal } from '@/components/QuickEnquiryModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_ICONS = {
  enquiry: MessageSquare, offer: DollarSign, inspection: Eye,
  sms: MessageSquare, notification: MessageSquare, note: StickyNote,
};

export default function BuyerProfile() {
  const { buyerId } = useParams();
  const navigate = useNavigate();
  const [buyer, setBuyer] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([
        axios.get(`${API}/buyers/${buyerId}`, { withCredentials: true }),
        axios.get(`${API}/buyers/${buyerId}/timeline`, { withCredentials: true }),
      ]);
      setBuyer(b.data);
      setTimeline(t.data || []);
    } catch (e) {
      toast.error('Failed to load buyer');
    }
  }, [buyerId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      setSavingNote(true);
      await axios.post(`${API}/buyers/${buyerId}/notes`, { text: noteText }, { withCredentials: true });
      setNoteText('');
      toast.success('Note added');
      load();
    } catch (e) {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  if (!buyer) return <div className="p-8 text-gray-400">Loading…</div>;

  const stats = [
    { label: 'Enquiries', value: buyer.enquiry_count || 0 },
    { label: 'Purchases', value: buyer.purchase_count || 0 },
    { label: 'Total Spent', value: `$${Number(buyer.total_spent || 0).toLocaleString()}` },
    { label: 'Avg Purchase', value: `$${Number(buyer.avg_purchase || 0).toLocaleString()}` },
  ];

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <button onClick={() => navigate('/buyers')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#474747] mb-4" data-testid="back-to-buyers">
        <ArrowLeft className="w-4 h-4" /> Buyers
      </button>

      <div className="bg-white rounded-xl border border-[#E9E9E9] p-6 mb-6" data-testid="buyer-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#474747] flex items-center gap-2">
              {buyer.name}
              {buyer.vip && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold" data-testid="vip-badge"><Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> VIP Buyer</span>}
            </h1>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
              {buyer.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{buyer.phone}</span>}
              {buyer.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{buyer.email}</span>}
              {buyer.facebook_profile && <span className="flex items-center gap-1"><Facebook className="w-3.5 h-3.5" />{buyer.facebook_profile}</span>}
            </div>
            {(buyer.interests || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="buyer-interests">
                {buyer.interests.map(i => (
                  <span key={i} className="px-2.5 py-1 rounded-full bg-[#F7FBEC] border border-[#C6E76C] text-xs font-medium text-[#3D4237]">{i}</span>
                ))}
              </div>
            )}
          </div>
          <Button onClick={() => setEnquiryOpen(true)} className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117] font-semibold" data-testid="buyer-log-enquiry-button">
            <Plus className="w-4 h-4 mr-1" /> Log Enquiry
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {stats.map(s => (
            <div key={s.label} className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-lg font-bold text-[#474747]">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#E9E9E9] p-6" data-testid="buyer-timeline">
          <h2 className="text-lg font-semibold text-[#474747] mb-4">Communication Timeline</h2>
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-500">No activity yet.</p>
          ) : (
            <div className="space-y-4">
              {timeline.map((ev, idx) => {
                const Icon = KIND_ICONS[ev.kind] || MessageSquare;
                return (
                  <div key={`${ev.created_at}-${idx}`} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#F7FBEC] border border-[#C6E76C] flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#3D4237]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#474747]">{ev.description}</p>
                      {ev.detail && <p className="text-sm text-gray-600 whitespace-pre-wrap">{ev.detail}</p>}
                      <p className="text-xs text-gray-400 mt-0.5">{fmt(ev.created_at)}{ev.channel ? ` • ${ev.channel}` : ''}{ev.status ? ` • ${ev.status}` : ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-[#E9E9E9] p-6 h-fit" data-testid="buyer-notes-card">
          <h2 className="text-lg font-semibold text-[#474747] mb-3">Add Internal Note</h2>
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} placeholder="e.g. Prefers weekend inspections…" data-testid="buyer-note-input" />
          <Button onClick={addNote} disabled={savingNote || !noteText.trim()} className="mt-2 w-full bg-[#1E2117] hover:bg-[#33382A] text-[#C6E76C] font-semibold" data-testid="buyer-note-save">
            {savingNote ? 'Saving…' : 'Save Note'}
          </Button>
        </div>
      </div>

      <QuickEnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} onSaved={load} preselectedBuyer={buyer} />
    </div>
  );
}
